import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores';
import { applyBranding } from '@/lib/theme';
import { isDirectSalePath, isReferencePath } from '@/lib/iframe-boot';

/**
 * Keeps the page's colours in step with the logged-in user's customer and
 * the current route.
 *
 * The Seller Creator learns the customer's branding with the user (login,
 * token refresh, iframe auto-login); applying it here means every seller
 * route takes the colours, and logging out drops back to the URL values or
 * the reference design. Admin and auth routes stay in the reference colours.
 *
 * Direct Sale has no user — its session exchange applies the shop's branding
 * itself (see api/directSale.ts). The hook must not touch it: it runs for
 * every route, and with the pathname as a dependency it would otherwise
 * overwrite the session branding on every navigation.
 */
export function useBrandingSync(): void {
  const branding = useAuthStore((state) => state.user?.customer?.branding);
  const { pathname } = useLocation();

  useEffect(() => {
    if (isDirectSalePath(pathname)) return;
    if (isReferencePath(pathname)) {
      applyBranding(null, { ignoreUrl: true });
      return;
    }
    applyBranding(branding ?? null);
  }, [branding, pathname]);
}
